/**
 * Keyboard model — the single source of truth for key geometry, finger
 * assignment and character → keystroke resolution.
 *
 * Everything visual (the on-screen keyboard, the hand guide, the heat map)
 * and everything pedagogical (which finger to coach, which key to highlight)
 * reads from this file, so the mapping can never disagree with itself.
 */
import type { FingerId, FingerMeta, KeyDef } from '../types';

export const FINGERS: Record<FingerId, FingerMeta> = {
  'l-pinky': { id: 'l-pinky', hand: 'left', label: 'left pinky', cssVar: '--f-l-pinky', slot: 0 },
  'l-ring': {
    id: 'l-ring',
    hand: 'left',
    label: 'left ring finger',
    cssVar: '--f-l-ring',
    slot: 1,
  },
  'l-middle': {
    id: 'l-middle',
    hand: 'left',
    label: 'left tall finger',
    cssVar: '--f-l-middle',
    slot: 2,
  },
  'l-index': { id: 'l-index', hand: 'left', label: 'left pointer', cssVar: '--f-l-index', slot: 3 },
  thumb: { id: 'thumb', hand: 'left', label: 'thumb', cssVar: '--f-thumb', slot: 4 },
  'r-index': {
    id: 'r-index',
    hand: 'right',
    label: 'right pointer',
    cssVar: '--f-r-index',
    slot: 3,
  },
  'r-middle': {
    id: 'r-middle',
    hand: 'right',
    label: 'right tall finger',
    cssVar: '--f-r-middle',
    slot: 2,
  },
  'r-ring': {
    id: 'r-ring',
    hand: 'right',
    label: 'right ring finger',
    cssVar: '--f-r-ring',
    slot: 1,
  },
  'r-pinky': { id: 'r-pinky', hand: 'right', label: 'right pinky', cssVar: '--f-r-pinky', slot: 0 },
};

/** Home-row resting keys, left to right. */
export const HOME_KEYS = ['a', 's', 'd', 'f', 'j', 'k', 'l', ';'] as const;

const k = (
  id: string,
  finger: FingerId,
  extra: Partial<Omit<KeyDef, 'id' | 'finger'>> = {}
): KeyDef => ({ id, label: id, finger, ...extra });

/**
 * US-QWERTY, four rows plus the space bar. Widths are in key units so the
 * renderer can lay the board out with flexbox and stay fluid at any size.
 */
export const KEY_ROWS: KeyDef[][] = [
  [
    k('`', 'l-pinky', { shiftLabel: '~' }),
    k('1', 'l-pinky', { shiftLabel: '!' }),
    k('2', 'l-ring', { shiftLabel: '@' }),
    k('3', 'l-middle', { shiftLabel: '#' }),
    k('4', 'l-index', { shiftLabel: '$' }),
    k('5', 'l-index', { shiftLabel: '%' }),
    k('6', 'r-index', { shiftLabel: '^' }),
    k('7', 'r-index', { shiftLabel: '&' }),
    k('8', 'r-middle', { shiftLabel: '*' }),
    k('9', 'r-ring', { shiftLabel: '(' }),
    k('0', 'r-pinky', { shiftLabel: ')' }),
    k('-', 'r-pinky', { shiftLabel: '_' }),
    k('=', 'r-pinky', { shiftLabel: '+' }),
    k('Backspace', 'r-pinky', { label: '⌫', width: 2 }),
  ],
  [
    k('Tab', 'l-pinky', { label: 'tab', width: 1.5 }),
    k('q', 'l-pinky'),
    k('w', 'l-ring'),
    k('e', 'l-middle'),
    k('r', 'l-index'),
    k('t', 'l-index'),
    k('y', 'r-index'),
    k('u', 'r-index'),
    k('i', 'r-middle'),
    k('o', 'r-ring'),
    k('p', 'r-pinky'),
    k('[', 'r-pinky', { shiftLabel: '{' }),
    k(']', 'r-pinky', { shiftLabel: '}' }),
    k('\\', 'r-pinky', { shiftLabel: '|', width: 1.5 }),
  ],
  [
    k('CapsLock', 'l-pinky', { label: 'caps', width: 1.75 }),
    k('a', 'l-pinky', { home: true }),
    k('s', 'l-ring', { home: true }),
    k('d', 'l-middle', { home: true }),
    k('f', 'l-index', { home: true, anchor: true }),
    k('g', 'l-index'),
    k('h', 'r-index'),
    k('j', 'r-index', { home: true, anchor: true }),
    k('k', 'r-middle', { home: true }),
    k('l', 'r-ring', { home: true }),
    k(';', 'r-pinky', { home: true, shiftLabel: ':' }),
    k("'", 'r-pinky', { shiftLabel: '"' }),
    k('Enter', 'r-pinky', { label: '⏎ enter', width: 2.25 }),
  ],
  [
    k('ShiftLeft', 'l-pinky', { label: '⇧ shift', width: 2.25 }),
    k('z', 'l-pinky'),
    k('x', 'l-ring'),
    k('c', 'l-middle'),
    k('v', 'l-index'),
    k('b', 'l-index'),
    k('n', 'r-index'),
    k('m', 'r-index'),
    k(',', 'r-middle', { shiftLabel: '<' }),
    k('.', 'r-ring', { shiftLabel: '>' }),
    k('/', 'r-pinky', { shiftLabel: '?' }),
    k('ShiftRight', 'r-pinky', { label: '⇧ shift', width: 2.75 }),
  ],
  [k(' ', 'thumb', { label: 'space', width: 9 })],
];

export const ALL_KEYS: KeyDef[] = KEY_ROWS.flat();

const KEY_BY_ID = new Map(ALL_KEYS.map((key) => [key.id, key]));

/**
 * character → the physical key that produces it, and whether Shift is needed.
 * Built once from KEY_ROWS so a change to the layout propagates automatically.
 */
export const CHAR_MAP: Record<string, { keyId: string; shift: boolean }> = (() => {
  const map: Record<string, { keyId: string; shift: boolean }> = {};
  for (const key of ALL_KEYS) {
    if (key.id.length !== 1) continue;
    map[key.id] = { keyId: key.id, shift: false };
    if (key.shiftLabel) map[key.shiftLabel] = { keyId: key.id, shift: true };
    // Letters: the uppercase form is the same key with Shift.
    const upper = key.id.toUpperCase();
    if (upper !== key.id) map[upper] = { keyId: key.id, shift: true };
  }
  return map;
})();

/** Which physical key produces `char` (or null if it is not on the board). */
export function keyForChar(char: string): KeyDef | null {
  const hit = CHAR_MAP[char];
  return hit ? (KEY_BY_ID.get(hit.keyId) ?? null) : null;
}

/** Does typing `char` require holding Shift? */
export function needsShift(char: string): boolean {
  return CHAR_MAP[char]?.shift ?? false;
}

/** Which finger types `char`. Falls back to the right pointer for oddities. */
export function fingerForChar(char: string): FingerId {
  if (char === ' ') return 'thumb';
  return keyForChar(char)?.finger ?? 'r-index';
}

/**
 * The Shift key to hold for `char` — always the one opposite the typing hand,
 * which is the habit that keeps both hands anchored. Returns null when the
 * character needs no Shift.
 */
export function shiftKeyForChar(char: string): 'ShiftLeft' | 'ShiftRight' | null {
  if (!needsShift(char)) return null;
  return FINGERS[fingerForChar(char)].hand === 'left' ? 'ShiftRight' : 'ShiftLeft';
}

/** Human-readable coaching line: "Use your left pointer for F." */
export function fingerHint(char: string): string {
  if (char === ' ') return 'Tap the space bar with your thumb.';
  const finger = FINGERS[fingerForChar(char)];
  const shift = shiftKeyForChar(char);
  const name = char === '\n' ? 'Enter' : char.toUpperCase();
  return shift
    ? `Hold ${shift === 'ShiftLeft' ? 'left' : 'right'} Shift with your ${
        shift === 'ShiftLeft' ? 'left' : 'right'
      } pinky, then press ${name} with your ${finger.label}.`
    : `Press ${name} with your ${finger.label}.`;
}

/** The hand that types `char` — drives which hand diagram animates. */
export function handForChar(char: string): 'left' | 'right' | 'both' {
  if (char === ' ') return 'both';
  if (needsShift(char)) return 'both';
  return FINGERS[fingerForChar(char)].hand;
}

/** Display form for the heat map / stats: spaces and newlines need a glyph. */
export function charLabel(char: string): string {
  if (char === ' ') return '␣';
  if (char === '\n') return '⏎';
  return char;
}
